import { useCallback, useMemo, useState } from "react"

interface ReadableRow {
	id: string
	readAt?: number | null
}

/** Snapshots rows as read, so they can outlive their place in the Unread query. */
export function keepRead<T extends ReadableRow>(
	kept: ReadonlyMap<string, T>,
	rows: readonly T[],
	at: number,
): Map<string, T> {
	const next = new Map(kept)
	for (const row of rows) next.set(row.id, { ...row, readAt: row.readAt ?? at })
	return next
}

/** A live row always wins over its snapshot, so marking it unread again shows the live state. */
export function withKeptRows<T extends ReadableRow>(
	rows: T[],
	kept: ReadonlyMap<string, T>,
): T[] {
	if (kept.size === 0) return rows
	const live = new Set(rows.map((row) => row.id))
	const gone = [...kept.values()].filter((row) => !live.has(row.id))
	return gone.length > 0 ? [...rows, ...gone] : rows
}

/**
 * Rows read in the Unread view stay listed, now read, until the scope (view and
 * filters) changes or the page reloads. A null scope keeps nothing.
 */
export function useKeptReadRows<T extends ReadableRow>(
	rows: T[],
	scope: string | null,
) {
	const [state, setState] = useState(() => ({
		scope,
		kept: new Map<string, T>(),
	}))
	let { kept } = state
	if (state.scope !== scope) {
		kept = new Map()
		setState({ scope, kept })
	}

	const keep = useCallback(
		(ids: readonly string[]) => {
			if (scope === null || ids.length === 0) return
			const wanted = new Set(ids)
			const read = rows.filter((row) => wanted.has(row.id))
			setState((prev) =>
				prev.scope === scope
					? { scope, kept: keepRead(prev.kept, read, Date.now()) }
					: prev,
			)
		},
		[rows, scope],
	)

	const forget = useCallback((ids: readonly string[]) => {
		setState((prev) => {
			if (!ids.some((id) => prev.kept.has(id))) return prev
			const next = new Map(prev.kept)
			for (const id of ids) next.delete(id)
			return { ...prev, kept: next }
		})
	}, [])

	const clear = useCallback(() => {
		setState((prev) =>
			prev.kept.size === 0 ? prev : { ...prev, kept: new Map() },
		)
	}, [])

	const merged = useMemo(() => withKeptRows(rows, kept), [rows, kept])
	return { rows: merged, keep, forget, clear }
}
